<template>
 <div
   ref="dotCard"
   class="product-dot"
   :style="{ top: `${position.y}%`, left: `${position.x}%` }"
   :class="{
     '--load-buy': isBuyProduct,
     '--bottom': position.bottom
   }"
 >
   <div class="product-dot-pin" @mouseover="getProduct"/>
   <div
     class="product-dot-card"
     :class="{'--loading': isLoadProduct}"
   >
     <template v-if="product && !isLoadProduct">
       <div class="product-dot-card__name h5 semibold">
         {{ product.name }}
       </div>
       <div
         class="product-dot-card__prices"
         :class="{'--discount': isVisibleOldPrice}"
       >
         <div class="--current">{{$numberFormat(productPrice)}} ₽</div>
         <div v-if="isVisibleOldPrice" class="--old">{{$numberFormat(productPriceOld)}} ₽</div>
       </div>
       <div class="product-dot-card__action">
         <button v-if="isVisibleBuy" class="btn btn-primary add" @click="addToCartProduct">
           В корзину
         </button>
         <button class="btn wish" @click="clickWish">
           <img v-if="isWish" src="~/assets/img/svg/common/heart-fill.svg" alt=""/>
           <img v-if="!isWish" src="~/assets/img/svg/common/heart.svg" alt=""/>
         </button>
         <div
           v-if="!isProductPage"
           @click="scrollToProduct"
           class="link"
         >
           К товару
         </div>
       </div>
     </template>
     <template v-if="isLoadProduct">
       <img src="~/assets/img/svg/loading/body-loading.svg" alt=""/>
     </template>
   </div>
 </div>
</template>

<script lang="ts" setup>
import { computed, type ComputedRef, onMounted, ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { useCommonStore } from '~/store/common';
import useCart from '#sitis/composables/useSitisCart';
import useSitisWish from '#sitis/composables/useSitisWish';
import collectionImageSizeAdmin from "~/constants/collection-image-size-admin.json";

const props = defineProps({
  info: {
    type: Object as any,
    default: () => {}
  },
  isProductPage: {
    type: Boolean,
    default: false,
  }
});
const {
  $api,
  $toast,
  $numberFormat
} = useNuxtApp();

const cartStore = useCart();
const wishStore = useSitisWish();
const commonStore = useCommonStore();

const dotCard: any = ref(null);
const product: any = ref(null);
const isLoadProduct = ref(true);
const isBuyProduct = ref(false);

const isVisibleBuy = computed(() => {
  if (product.value?.has_options && !props?.info?.option) {
    return false
  }
  if (product.value?.has_options) {
    const productOptions = product?.value?.options || [];

    let countAvailableOption = 0;
    Object.keys(props?.info?.option || {}).map((optionKey: string) => {
      const optionValue: any = props?.info?.option[optionKey];
      const isAvailableValue = Boolean((productOptions.find((t: any) => String(t.id) === String(optionKey))?.variants || []).find((t: any) => String(t.id) === String(optionValue)));
      if (isAvailableValue) {
        countAvailableOption = countAvailableOption + 1;
      }
    });

    return Boolean(countAvailableOption === Object.keys(props?.info?.option || {}).length)
  }
  return true
});
const isVisibleOldPrice = computed(() => {
  return Boolean(product.value.price_old && product.value.price_old > product.value.price)
})

const getProduct = async () => {
  if (!isLoadProduct.value || product.value?.id) {
    return
  }
  const fields = ['id', 'name', 'price', 'price_old', 'image_id', 'has_options', 'status'];
  const _product = await $api.agent.get(`/products/${ props.info.product_id }?noPushRecent=1&fields=${fields.join(",")}`).then((res: any) => {
    return res?._data
  }).catch(() => {
    return null
  });
  if (!_product) {
    position.value = {x: 0, y: 0, bottom: false};
    return
  }

  product.value = _product;
  isLoadProduct.value = false;
};
const addToCartProduct = async () => {
  isBuyProduct.value = true;

  const options = props?.info?.option || {};

  let body: any = {
    productId: props.info?.product_id,
    quantity: 1
  };
  if (Object.keys(options).length > 0) {
    body.options = {};
    Object.keys(options).map((optionId: any) => {
      body.options[optionId] = options[optionId]
    })
  };

  const resAdd: any = await cartStore.addToCart(body).catch((err) => {
    return {error: err._data}
  });
  if (resAdd?.error) {
    isBuyProduct.value = false;
    let errorMessage = (resAdd?.error || []).map((t: any) => t.message).join(', ');
    if (!errorMessage) {
      errorMessage = "Ошибка сервера"
    }
    $toast.error(errorMessage);
    return
  }

  await cartStore.getCart();
  isBuyProduct.value = false;
  $toast.success("Товар добавлен в корзину");
};

const position = ref({ x: 0, y: 0, bottom: false });
const setPosition = () => {
  const isPercentVersion = true;

  const parent = dotCard.value.parentElement;
  const parentSize = parent.getBoundingClientRect();

  const differenceWidth = (100*(parentSize.width-collectionImageSizeAdmin.width)/collectionImageSizeAdmin.width);
  const differenceHeight = (100*(parentSize.height-collectionImageSizeAdmin.height)/collectionImageSizeAdmin.height);

  const positionTopPx = props?.info?.top + ((props?.info?.top / 100) * differenceHeight);
  const positionLeftPx = props?.info?.left + ((props?.info?.left / 100) * differenceWidth);

  const positionTopPercent = Boolean(isPercentVersion) ? props?.info?.top : (positionTopPx / parentSize.height) * 100;
  const positionLeftPercent = Boolean(isPercentVersion) ? props?.info?.left : (positionLeftPx / parentSize.width) * 100;

  let isBottomPosition = false;
  if ((positionTopPx - 160) <= 0) {
    isBottomPosition = true;
  }

  position.value = {x: positionLeftPercent, y: positionTopPercent, bottom: isBottomPosition};
};
onMounted(() => {
  setPosition();
});

const isWish = computed(() => {
  return Boolean(wishStore.isInWish(props.info.product_id))
});
const clickWish = async () => {
  if (isWish.value) {
    await wishStore.removeItemFromWishList(props.info.product_id)
  } else {
    await wishStore.addToWishList({id: props.info.product_id})
  }
  await wishStore.getWishList();
};

const scrollToProduct = () => {
  const productCard = document.querySelector(`[data-product-id="${props.info.product_id}"]`);
  if (!productCard) {
    return
  }

  const currentScroll = window.scrollY;
  const headerHeight = document.querySelector('.header')?.getBoundingClientRect().height || 100;
  const productCardPosition = productCard.getBoundingClientRect();
  const newScrollPosition = productCardPosition.top + currentScroll - headerHeight - 160;

  window.scrollTo({
    top: newScrollPosition,
    left: 0,
    behavior: 'smooth'
  });
}


const productPrice: ComputedRef<number | null> = computed(() => {
  let _optionsPrice = Object.keys(props?.info?.option || {}).map((optionKey: string) => {
    const optionValue = props?.info?.option?.[optionKey];
    const optionVariant = ((product?.value?.options || []).find((t: any) => String(t?.id) === String(optionKey))?.variants || []).find((t: any) => String(t?.id) === String(optionValue));
    return optionVariant?.price || 0
  });
  _optionsPrice = _optionsPrice.reduce((val: number, item: number) => {
    return val + item
  }, 0)
  return _optionsPrice + product.value?.price || 0
})
const productPriceOld: ComputedRef<number | null> = computed(() => {
  if (!isVisibleOldPrice.value) {
    return null
  }

  let _optionsPrice = Object.keys(props?.info?.option || {}).map((optionKey: string) => {
    const optionValue = props?.info?.option?.[optionKey];
    const optionVariant = ((product?.value?.options || []).find((t: any) => String(t?.id) === String(optionKey))?.variants || []).find((t: any) => String(t?.id) === String(optionValue));
    return optionVariant?.price_old || optionVariant?.price || 0
  });
  _optionsPrice = _optionsPrice.reduce((val: number, item: number) => {
    return val + item
  }, 0)
  return _optionsPrice + product.value?.price_old || 0
})
</script>

<style lang="scss" scoped>
.product-dot {
  position: absolute;

  &.--bottom {
    .product-dot-card {
      top: 20px;
      bottom: initial;
      border-radius: 0 10px 10px 10px;

      &:after {
        top: initial;
        bottom: 100%;
      }
      &:before {
        top: -6px;
        transform: scaleX(1) scaleY(-1);
      }
    }
  }
  &.--load-buy .product-dot-card:after {
    top: 0; left: 0;
    width: 100%; height: 100%;
    background-color: rgba(255,255,255,0.5);
    background-image: url("@/assets/img/svg/loading/round-loading.svg");
    background-size: 24px;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 10px;
  }
  &:hover .product-dot-card {
    display: block;
  }
}

.product-dot-pin {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  width: 13px;
  height: 13px;
  background-color: white;
  cursor: pointer;
  border-radius: 100%;
  box-shadow: 0 0 0 3px rgb(255 255 255 / 40%);

  &::after {
    content: "";
    position: absolute;
    background-color: black;
    top: 3px; left: 3px;
    width: 7px; height: 7px;
    border-radius: 100%;
  }
}

.product-dot-card {
  display: none;
  position: absolute;
  left: 5px; bottom: 10px;
  width: 100vw;
  max-width: 300px;
  z-index: 91;
  padding: 16px;
  box-sizing: border-box;
  background-color: #FFF;
  border-radius: 10px 10px 10px 0;
  box-shadow: 4px 6px 31px 0px #0000000D;

  &::after {
    content: "";
    position: absolute;
    top: 100%; left: -20px; right: 0;
    height: 20px;
  }
  &::before {
    content: "";
    position: absolute;
    top: 100%; left: 0;
    background-image: url("@/assets/img/svg/additionally/tooltip-circle-bottom-left.svg");
    background-size: contain;
    background-repeat: no-repeat;
    width: 14px; height: 6px;
    filter: invert(1);
  }
  &.--loading {
    height: 64px;
    img {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 48px; height: 48px;
      object-fit: contain;
    }
  }
}

.product-dot-card__name {
  margin-bottom: 16px;
}

.product-dot-card__prices {
  display: flex;
  align-items: flex-end;
  margin-bottom: 24px;

  .--current {
    font-size: 16px;
    font-weight: 600;
    line-height: 120%;
  }

  .--old {
    margin-left: 8px;
    color: #808080;
    font-variant-numeric: lining-nums proportional-nums;
    font-size: 12px;
    line-height: 20px;
    text-decoration: line-through;
  }

  &.--discount .--current {
    color: #ED908C;
  }
}

.product-dot-card__action {
  display: flex;
  align-items: center;

  .btn {
    height: 40px;
    border: none;
    padding: 0 34px;
    color: #FFF;
    font-size: 12px;
    line-height: 100%;
    font-weight: 600;
  }

  .add {
    margin-right: 10px;
  }
  .wish {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background-color: #F5F5F5;
    width: 40px;
    height: 40px;
    cursor: pointer;

    img {
      filter: invert(32%) sepia(46%) saturate(2754%) hue-rotate(339deg) brightness(88%) contrast(99%);
    }

    &:hover {
      background-color: #EBEBEB;
    }
  }

  .link {
    cursor: pointer;
    border-bottom: 1px solid;
    margin-left: auto;
    color: #989898;
    font-variant-numeric: lining-nums proportional-nums;
    font-size: 12px;
    line-height: 100%;
    transition: all 0.3s;

    &:hover {
      border-bottom-color: transparent;
    }
  }
}
</style>
